import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { useTranslation } from '../hooks/useTranslation';
import { API_URL, getUploadUrl } from '../config/api';
import CurrencyLogo from './CurrencyLogo';
import './css/search.css';
import FooterNote from './FooterNote';

// Placeholder store image for branches
const placeholderStoreImage = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='200' viewBox='0 0 200 200'%3E%3Crect fill='%23e0e0e0' width='200' height='200'/%3E%3Cpath fill='%23b0b0b0' d='M50 50h100v100H50z'/%3E%3Cpath fill='%23999' d='M60 60h80v20H60zm0 30h80v20H60zm0 30h80v20H60z'/%3E%3C/svg%3E";

const Search = () => {
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [hasSearched, setHasSearched] = useState(false);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (searchTerm.trim()) {
      performSearch();
    } else {
      setProducts([]);
      setCategories([]);
      setBranches([]);
    }
  }, [searchTerm, language]);

  const performSearch = async () => {
    if (!searchTerm.trim()) {
      return;
    }

    setLoading(true);
    try {
      const searchLower = searchTerm.toLowerCase();

      // Fetch all data
      const [productsRes, categoriesRes, branchesRes] = await Promise.all([
        axios.get(`${API_URL}/api/products`),
        axios.get(`${API_URL}/api/categories`),
        axios.get(`${API_URL}/api/branches`)
      ]);

      // Search products
      const filteredProducts = productsRes.data
        .filter(product => {
          if (!product.is_active) return false;
          const nameAr = (product.name_ar || '').toLowerCase();
          const nameEn = (product.name_en || '').toLowerCase();
          const descAr = (product.description_ar || '').toLowerCase();
          const descEn = (product.description_en || '').toLowerCase();
          const ingredientsAr = Array.isArray(product.ingredients_ar) 
            ? product.ingredients_ar.map(i => i.toLowerCase()).join(' ')
            : '';
          const ingredientsEn = Array.isArray(product.ingredients_en)
            ? product.ingredients_en.map(i => i.toLowerCase()).join(' ')
            : '';

          return nameAr.includes(searchLower) ||
                 nameEn.includes(searchLower) ||
                 descAr.includes(searchLower) ||
                 descEn.includes(searchLower) ||
                 ingredientsAr.includes(searchLower) ||
                 ingredientsEn.includes(searchLower);
        })
        .map(product => {
          const category = categoriesRes.data.find(c => c.id === product.category_id);
          return {
            id: product.id,
            name: language === 'ar' ? product.name_ar : product.name_en,
            description: language === 'ar' ? product.description_ar : product.description_en,
            price: parseFloat(product.price) || 0,
            calories: product.calories || null,
            image: product.image ? getUploadUrl(product.image) : null,
            categoryKey: category ? category.key_name : '',
            categoryName: category 
              ? (language === 'ar' ? category.name_ar : category.name_en)
              : '',
            categoryLink: category ? `/${category.key_name}` : '/'
          };
        });

      // Search categories
      const filteredCategories = categoriesRes.data
        .filter(category => {
          if (!category.is_active) return false;
          const nameAr = (category.name_ar || '').toLowerCase();
          const nameEn = (category.name_en || '').toLowerCase();
          return nameAr.includes(searchLower) || nameEn.includes(searchLower);
        })
        .map(category => ({
          id: category.id,
          name: language === 'ar' ? category.name_ar : category.name_en,
          link: `/${category.key_name}`,
          image: category.image ? getUploadUrl(category.image) : null
        }));

      // Search branches
      const filteredBranches = branchesRes.data
        .filter(branch => {
          if (!branch.is_active) return false;
          const nameAr = (branch.name_ar || '').toLowerCase();
          const nameEn = (branch.name_en || '').toLowerCase();
          const locAr = (branch.location_ar || '').toLowerCase();
          const locEn = (branch.location_en || '').toLowerCase();
          const phone = (branch.phone || '').toLowerCase();
          return nameAr.includes(searchLower) ||
                 nameEn.includes(searchLower) ||
                 locAr.includes(searchLower) ||
                 locEn.includes(searchLower) ||
                 phone.includes(searchLower);
        })
        .map(branch => ({
          id: branch.id,
          name: language === 'ar' ? branch.name_ar : branch.name_en,
          location: language === 'ar' ? branch.location_ar : branch.location_en,
          phone: branch.phone || '',
          link: `/branch/${branch.id}`,
          image: branch.image ? getUploadUrl(branch.image) : placeholderStoreImage
        }));

      setProducts(filteredProducts);
      setCategories(filteredCategories);
      setBranches(filteredBranches);
    } catch (error) {
      console.error('Error performing search:', error);
    } finally {
      setLoading(false);
    }
  };

  // Auto-focus search input on mount
  useEffect(() => {
    const searchInput = document.querySelector('.search-input');
    if (searchInput) {
      searchInput.focus();
    }
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setHasSearched(true);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setHasSearched(false);
    setProducts([]);
    setCategories([]);
    setBranches([]);
  };

  const totalResults = products.length + categories.length + branches.length;

  return (
    <section className='main-view'>
      <section className='search-page'>
        <div className='container-fluid'>
          <div className='search-page-content'>
            {/* Header */}
            <div className='search-header'>
              <button 
                className='back-btn'
                onClick={() => navigate('/')}
              >
                {language === 'ar' ? '← ' : ''}{t('nav.back')}{language === 'en' ? ' ←' : ''}
              </button>
              <h1 className='search-title'>{t('search.title')}</h1>
            </div>

            {/* Search Form */}
            <form className='search-form' onSubmit={handleSearch}>
              <div className='search-input-wrapper'>
                <MagnifyingGlassIcon className='search-icon' />
                <input
                  type='text'
                  className='search-input'
                  placeholder={t('search.placeholder')}
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setHasSearched(true);
                  }}
                />
                {searchTerm && (
                  <button
                    type='button'
                    className='clear-search-btn'
                    onClick={clearSearch}
                  >
                    <XMarkIcon className='icon' />
                  </button>
                )}
              </div>
            </form>

            {/* Results */}
            {hasSearched && (
              <div className='search-results'>
                {loading ? (
                  <div className='loading-message'>
                    <p>{t('search.loading') || 'Searching...'}</p>
                  </div>
                ) : searchTerm.trim() === '' ? (
                  <div className='no-results'>
                    <p>{t('search.enterSearch')}</p>
                  </div>
                ) : totalResults === 0 ? (
                  <div className='no-results'>
                    <p>{t('search.noResults')}</p>
                    <p className='no-results-subtitle'>{t('search.tryDifferent')}</p>
                  </div>
                ) : (
                  <>
                    {/* Results Summary */}
                    <div className='results-summary'>
                      <p>{t('search.resultsFound')} {totalResults} {totalResults === 1 ? t('search.result') : t('search.results')}</p>
                    </div>

                    {/* Products Results */}
                    {products.length > 0 && (
                      <div className='results-section'>
                        <h2 className='results-section-title'>{t('search.products')} ({products.length})</h2>
                        <div className='row'>
                          {products.map((product) => (
                            <div className='col-lg-4 col-md-6 col-12' key={product.id}>
                              <div 
                                className='search-result-item product-item'
                                onClick={() => navigate(product.categoryLink)}
                              >
                                {product.image && (
                                  <img 
                                    src={product.image} 
                                    alt={product.name} 
                                    className='result-image' 
                                  />
                                )}
                                <div className='result-info'>
                                  <h3 className='result-name'>{product.name}</h3>
                                  {product.categoryName && (
                                    <p className='result-category'>{product.categoryName}</p>
                                  )}
                                  {product.price > 0 && (
                                    <p className='result-price'>
                                      {product.price.toFixed(2)} <CurrencyLogo />
                                    </p>
                                  )}
                                  {product.calories && (
                                    <p className='result-calories'>
                                      {t('products.calories') || 'Calories'}: {product.calories}
                                    </p>
                                  )}
                                  {product.description && (
                                    <p className='result-description'>{product.description}</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Categories Results */}
                    {categories.length > 0 && (
                      <div className='results-section'>
                        <h2 className='results-section-title'>{t('search.categories')} ({categories.length})</h2>
                        <div className='row'>
                          {categories.map((category) => (
                            <div className='col-lg-4 col-md-6 col-12' key={category.id}>
                              <div 
                                className='search-result-item category-item'
                                onClick={() => navigate(category.link)}
                              >
                                {category.image && (
                                  <img 
                                    src={category.image} 
                                    alt={category.name} 
                                    className='result-image' 
                                  />
                                )}
                                <div className='result-info'>
                                  <h3 className='result-name'>{category.name}</h3>
                                  <p className='result-type'>{t('search.category')}</p>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Branches Results */}
                    {branches.length > 0 && (
                      <div className='results-section'>
                        <h2 className='results-section-title'>{t('search.branches')} ({branches.length})</h2>
                        <div className='row'>
                          {branches.map((branch) => (
                            <div className='col-lg-4 col-md-6 col-12' key={branch.id}>
                              <div 
                                className='search-result-item branch-item'
                                onClick={() => navigate(branch.link)}
                              >
                                <img 
                                  src={branch.image} 
                                  alt={branch.name} 
                                  className='result-image' 
                                />
                                <div className='result-info'>
                                  <h3 className='result-name'>{branch.name}</h3>
                                  <p className='result-location'>{branch.location}</p>
                                  {branch.phone && (
                                    <p className='result-phone'>{branch.phone}</p>
                                  )}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      </section>
      <FooterNote />
    </section>
  );
};

export default Search;
